import { z } from 'zod/v4'

import { ATTRIBUTE_SIGN_VALUES } from '../../contracts/transaction-attributes/denominations.js'
import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Atributos» (`admin.attributes`): the economic vocabulary every import adapter maps its own words onto
 * (docs/IMPORTS.md §4). Each row carries its flags, its label per locale and the active GLOBAL adapters whose rules
 * name it, so the superadmin reads the vocabulary map in one place. What an attribute means for TAX is not here: it is
 * per jurisdiction and period, in the tax rule sets. Strict.
 */

export const attributeAdapterUsageSchema = z.strictObject({
  /** The source document's id: its page is `/admin/system/adapters/{chain|exchange}/:id`. */
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  /** `csv` | `cex` | `dex` | `chain`. */
  kind: z.string(),
  /** The active version whose rules name the attribute. */
  version: z.number().int(),
})

export const adminAttributeRowSchema = z.strictObject({
  key: z.string(),
  /** The label in the reader's language (falls back to English, then the key). */
  name: z.string(),
  category: z.string(),
  /** Derived from the legs the attribute moves. */
  sign: z.enum(ATTRIBUTE_SIGN_VALUES),
  requiresIncoming: z.boolean(),
  requiresOutgoing: z.boolean(),
  allowsCommission: z.boolean(),
  requiresCostBasis: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  /** Label per locale code. */
  labels: z.record(z.string(), z.string()),
  usedBy: z.array(attributeAdapterUsageSchema),
})

export const adminLocaleOptionSchema = z.strictObject({ code: z.string(), name: z.string() })

export const adminAttributesScreen = defineScreen({
  id: 'admin.attributes',
  path: '/api/screens/admin/attributes',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    attributes: screenSection(z.array(adminAttributeRowSchema)),
    locales: screenSection(z.array(adminLocaleOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.attributes'],
  budget: { requests: 1, statements: 8, bytes: 120_000, p95Ms: 300 },
  summary: 'Transaction attributes: the vocabulary with its flags, labels and the adapters that speak it',
})

export type AdminAttributeRow = z.infer<typeof adminAttributeRowSchema>
export type AttributeAdapterUsage = z.infer<typeof attributeAdapterUsageSchema>
export type AdminLocaleOption = z.infer<typeof adminLocaleOptionSchema>
export type AdminAttributesData = ScreenData<typeof adminAttributesScreen>
